import type { Expense } from './types';

export const MAX_RECEIPT_BYTES = 10 * 1024 * 1024;
export const DOCUMENT_TYPES = ['receipt', 'invoice', 'eob', 'payment_proof', 'prescription', 'other'];
export const CATEGORIES = ['medical', 'dental', 'vision', 'prescriptions', 'equipment', 'otc'];
export type ReviewStatus = 'needs_review' | 'eligible' | 'ineligible';
export interface ReceiptItem {
    description: string;
    total: number;
    category: string | null;
    eligibility_status: ReviewStatus;
}
export interface ExpenseDraft {
    date_of_service: string;
    provider: string | null;
    patient_name: string | null;
    amount: number;
    category: Expense['category'];
    family_member: string | null;
    hsa_eligible: number;
    eligibility_confidence: number | null;
    eligibility_reasoning: string | null;
    items: ReceiptItem[];
}

export async function sha256(data: ArrayBuffer | Uint8Array | string): Promise<string> {
    const bytes = typeof data === 'string' ? new TextEncoder().encode(data) : data;
    return Array.from(new Uint8Array(await crypto.subtle.digest('SHA-256', bytes as BufferSource)))
        .map(b => b.toString(16).padStart(2, '0')).join('');
}

function text(value: unknown, max = 500): string | null {
    if (value == null || value === '') return null;
    if (typeof value !== 'string' || value.length > max) throw new Error('Invalid text field');
    return value.trim() || null;
}
function money(value: unknown): number {
    if (typeof value !== 'number' || !Number.isFinite(value) || value < 0 || value > 1e9 ||
        Math.abs(value * 100 - Math.round(value * 100)) > 0.00001) throw new Error('Invalid amount');
    return value;
}
function category(value: unknown): Expense['category'] {
    if (value == null || value === '' || value === 'other') return null;
    if (typeof value !== 'string' || !CATEGORIES.includes(value)) throw new Error('Invalid category');
    return value as Expense['category'];
}
export function validateDraft(input: unknown): ExpenseDraft {
    if (!input || typeof input !== 'object' || Array.isArray(input)) throw new Error('Invalid expense');
    const body = input as Record<string, unknown>;
    const date = body.date_of_service;
    if (typeof date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(date) ||
        !Number.isFinite(Date.parse(date)) || new Date(date).toISOString().slice(0, 10) !== date) {
        throw new Error('Provide a valid date of service');
    }
    const items = body.items ?? [];
    if (!Array.isArray(items) || items.length > 200) throw new Error('Invalid line items');
    return {
        date_of_service: date, provider: text(body.provider), patient_name: text(body.patient_name),
        amount: money(body.amount), category: category(body.category), family_member: text(body.family_member),
        hsa_eligible: body.hsa_eligible === 1 ? 1 : 0,
        eligibility_confidence: null,
        eligibility_reasoning: text(body.eligibility_reasoning, 2000) || 'Requires manual eligibility and payment review.',
        items: items.map(item => {
            if (!item || typeof item !== 'object') throw new Error('Invalid line item');
            const description = text(item.description, 1000);
            const status = item.eligibility_status ?? 'needs_review';
            if (!description || !['needs_review', 'eligible', 'ineligible'].includes(status)) throw new Error('Invalid line item');
            return { description, total: money(item.total), category: category(item.category), eligibility_status: status };
        }),
    };
}

// Sniff a small allowlist. MIME headers and extensions are not a trust boundary.
export function receiptMime(bytes: Uint8Array): string | null {
    const starts = (...values: number[]) => values.every((v, i) => bytes[i] === v);
    if (starts(0x25, 0x50, 0x44, 0x46, 0x2d)) return 'application/pdf';
    if (starts(0xff, 0xd8, 0xff)) return 'image/jpeg';
    if (starts(0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a)) return 'image/png';
    if (starts(0x49, 0x49, 0x2a, 0) || starts(0x4d, 0x4d, 0, 0x2a)) return 'image/tiff';
    const box = new TextDecoder().decode(bytes.slice(4, 12));
    if (/^ftyp(heic|heix|hevc|hevx|mif1|msf1)$/.test(box)) return 'image/heic';
    return null;
}
